import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, CircleAlert, Copy, CreditCard, ExternalLink, Info, Landmark, ShieldCheck, WalletCards } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/Button";
import { Modal } from "../components/ui/Modal";
import { useCredentials } from "../contexts/AuthContext";
import { useNotification } from "../contexts/NotificationContext";
import { hasBillingDetails, hasWaitingBillingDetails } from "../domain/billing";
import { createGoPayPayment, errorMessage, getBilling, getCredit, getGoPayPaymentStatus, getPaymentCapabilities, getPendingGoPayAmount, getVariableSymbol } from "../lib/api";
import type { GoPayPayment } from "../lib/api";

const bankAccounts = [
  { currency: "CZK", account: "2301102018/2010", iban: "CZ9020100000002301102018", bic: "FIOBCZPPXXX" },
  { currency: "EUR", account: "2501102020/2010", iban: "CZ2720100000002501102020", bic: "FIOBCZPPXXX" },
];

export function PaymentsPage() {
  const credentials = useCredentials();
  const { notify } = useNotification();
  const [amount, setAmount] = useState(500);
  const [searchParams, setSearchParams] = useSearchParams();
  const returnedPaymentValue = searchParams.get("id");
  const returnedPaymentId = returnedPaymentValue && /^[0-9]{1,64}$/.test(returnedPaymentValue) ? returnedPaymentValue : null;
  const paymentResultKey = returnedPaymentId ? `cloud4smart.gopay-result.${returnedPaymentId}` : null;
  const paymentDialogOpen = Boolean(paymentResultKey && sessionStorage.getItem(paymentResultKey) !== "shown");
  const credit = useQuery({ queryKey: ["credit", credentials.login], queryFn: () => getCredit(credentials), refetchInterval: 30_000 });
  const billing = useQuery({ queryKey: ["billing", credentials.login], queryFn: () => getBilling(credentials) });
  const billingHasDetails = hasBillingDetails(billing.data);
  const billingMissing = billing.data !== undefined && !billingHasDetails;
  const variableSymbol = useQuery({ queryKey: ["variable-symbol", credentials.login], queryFn: () => getVariableSymbol(credentials), enabled: billingHasDetails });
  const capabilities = useQuery({
    queryKey: ["payment-capabilities"],
    queryFn: getPaymentCapabilities,
    retry: false,
    staleTime: 5 * 60_000,
  });
  const pendingGoPay = useQuery({
    queryKey: ["gopay-pending", credentials.login],
    queryFn: getPendingGoPayAmount,
    enabled: capabilities.data?.goPay.environment === "production",
    refetchInterval: 5_000,
    retry: 1,
  });
  const paymentStatus = useQuery({
    queryKey: ["gopay-payment", returnedPaymentId],
    queryFn: () => getGoPayPaymentStatus(returnedPaymentId!),
    enabled: Boolean(returnedPaymentId && paymentDialogOpen),
    retry: 1,
  });
  const createPayment = useMutation({
    mutationFn: () => createGoPayPayment(amount),
    onSuccess: (payment) => {
      if (!payment.gatewayUrl) throw new Error("GoPay nevrátilo adresu platební brány.");
      window.location.assign(payment.gatewayUrl);
    },
  });
  const billingWaiting = hasWaitingBillingDetails(billing.data);
  const goPayEnabled = billing.data?.verified === true && !billingWaiting && capabilities.data?.goPay.enabled === true;
  const validAmount = Number.isInteger(amount) && amount >= 100 && amount <= 10_000;
  const goPaySandbox = capabilities.data?.goPay.environment === "sandbox";
  const goPayActivationOnly = capabilities.data?.goPay.environment === "production" && capabilities.data.goPay.recordOnly;
  const goPayLive = capabilities.data?.goPay.environment === "production" && capabilities.data.goPay.accountingEnabled;
  const copy = async (value: string, label: string) => { await navigator.clipboard.writeText(value); notify(`${label} byl zkopírován.`, "success"); };

  useEffect(() => {
    if (!returnedPaymentId || !paymentResultKey || paymentDialogOpen) return;
    const next = new URLSearchParams(searchParams);
    next.delete("id");
    setSearchParams(next, { replace: true });
  }, [paymentDialogOpen, paymentResultKey, returnedPaymentId, searchParams, setSearchParams]);

  const dismissPaymentStatus = () => {
    if (paymentResultKey) sessionStorage.setItem(paymentResultKey, "shown");
    const next = new URLSearchParams(searchParams);
    next.delete("id");
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="page payments-page">
      <header className="page-header"><div><span className="page-kicker">Finance</span><h1>Platby a kredit</h1><p>Doplnění předplaceného kreditu a platební údaje.</p></div></header>
      <section className="balance-hero"><div><span className="balance-hero__icon"><WalletCards /></span><span><small>Aktuální kredit</small><strong>{credit.data === undefined ? "—" : `${credit.data.toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Kč`}</strong>{pendingGoPay.data !== undefined && pendingGoPay.data > 0 && <span className="balance-hero__pending">Nezaúčtováno: <b>{pendingGoPay.data.toLocaleString("cs-CZ")} Kč</b></span>}<em>Kredit se aktualizuje automaticky po přijetí platby.</em></span></div><ShieldCheck /></section>

      {billingMissing && <div className="verification-panel payment-billing-alert" role="alert"><span><CircleAlert /></span><div><h2>Pro placený provoz vyplňte fakturační údaje</h2><p>Bez nich nedokážeme bezpečně přiřadit platbu ani vystavit správný doklad. Po odeslání údajů zobrazíme informace pro bankovní převod; GoPay zpřístupníme až po jejich schválení.</p><Link to="/app/billing">Doplnit fakturační údaje <ExternalLink /></Link></div></div>}

      <div className="payment-heading"><div><h2>Zvolte způsob dobití</h2><p>Bankovní převod doporučujeme – je bez poplatku.</p></div></div>
      {(variableSymbol.error || billing.error) && <div className="error-panel"><strong>Platební údaje se nepodařilo načíst</strong><p>{errorMessage(variableSymbol.error ?? billing.error)}</p></div>}
      <div className="payment-methods">
        <section className={`payment-card ${billingMissing ? "payment-card--disabled" : "payment-card--recommended"}`}>
          {!billingMissing && <div className="recommended-ribbon"><Check /> Doporučeno · bez poplatku</div>}
          <header><span><Landmark /></span><div><h2>Bankovní převod</h2><p>Nejvýhodnější způsob doplnění kreditu</p></div></header>
          {billingMissing ? (
            <div className="gopay-locked bank-transfer-locked"><CircleAlert /><h3>Nejdříve doplňte fakturační údaje</h3><p>Čísla účtů, IBAN, BIC a variabilní symbol zobrazíme, jakmile fakturační údaje odešlete.</p><Link to="/app/billing">Doplnit fakturační údaje <ExternalLink /></Link></div>
          ) : <>
            <div className="fee-row"><span>Poplatek za platbu</span><strong>0 Kč</strong></div>
            <div className="bank-tabs">
              {bankAccounts.map((bank) => <article key={bank.currency}><div className="bank-tabs__head"><BadgeFlag currency={bank.currency} /><strong>Účet v {bank.currency}</strong></div><dl><CopyRow label="Číslo účtu" value={bank.account} onCopy={copy} /><CopyRow label="IBAN" value={bank.iban} onCopy={copy} /><CopyRow label="BIC / SWIFT" value={bank.bic} onCopy={copy} /><CopyRow label="Variabilní symbol" value={variableSymbol.data ?? "Načítám…"} onCopy={variableSymbol.data ? copy : undefined} highlight /></dl></article>)}
            </div>
            <div className="info-box"><Info /><p>Pro automatické přiřazení platby vždy uveďte svůj variabilní symbol. Připsání závisí na zpracování bankovního převodu.</p></div>
          </>}
        </section>

        <section className={`payment-card payment-card--gopay ${!goPayEnabled ? "payment-card--disabled" : ""}`}>
          <header><span><CreditCard /></span><div><h2>Online platba přes GoPay</h2><p>Rychlá alternativa platební kartou</p></div></header>
          <div className="fee-row"><span>Platební brána</span><strong>GoPay</strong></div>
          {billingMissing ? (
            <div className="gopay-locked"><CircleAlert /><h3>Nejdříve doplňte fakturační údaje</h3><p>Údaje potřebujeme k identifikaci plátce a vystavení dokladu. GoPay zpřístupníme až po jejich schválení.</p><Link to="/app/billing">Doplnit fakturační údaje <ExternalLink /></Link></div>
          ) : billingWaiting ? (
            <div className="gopay-locked"><ShieldCheck /><h3>Čeká na ověření údajů</h3><p>Online platbu zpřístupníme po ručním ověření fakturačních údajů. Bankovní převod lze použít ihned.</p><a href="/app/billing">Zobrazit fakturační údaje <ExternalLink /></a></div>
          ) : !billing.data?.verified ? (
            <div className="gopay-locked"><ShieldCheck /><h3>Fakturační údaje nejsou schválené</h3><p>GoPay zpřístupníme až po ručním schválení fakturačních údajů. Bankovní převod můžete použít už nyní.</p><Link to="/app/billing">Zobrazit fakturační údaje <ExternalLink /></Link></div>
          ) : !goPayEnabled ? (
            <div className="gopay-locked"><ShieldCheck /><h3>GoPay se připravuje</h3><p>Bránu zobrazíme až po bezpečném zapojení produkčního účtu a automatického připsání kreditu. Převod bez poplatku je dostupný už nyní.</p></div>
          ) : (
            <>
              <div className="info-box"><Info /><p>
                {goPaySandbox ? <><strong>Testovací režim:</strong> GoPay používá pouze fiktivní peníze. Kredit ani účetní doklad nevznikne.</> : goPayActivationOnly ? <><strong>Aktivační kontrola ostré brány:</strong> Bránu pouze otevřete a zavřete. Platbu nedokončujte; v tomto režimu by se nepřipsala ke kreditu.</> : <><strong>Platba kartou:</strong> Po úspěšném zaplacení částku automaticky připíšeme ke kreditu a připravíme účetní doklad.</>}
              </p></div>
              <div className="amount-picker"><span>Částka k dobití</span><div>{[100, 200, 500, 1000, 2000, 5000].map((value) => <button key={value} type="button" className={amount === value ? "active" : ""} onClick={() => setAmount(value)}>{value.toLocaleString("cs-CZ")} Kč</button>)}</div><label><span>Vlastní částka</span><div><input type="number" min="100" max="10000" step="1" value={amount} onChange={(event) => setAmount(Number(event.target.value))} /><em>Kč</em></div></label></div>
              {createPayment.error && <div className="form-error">{errorMessage(createPayment.error)}</div>}
              <Button variant="primary" disabled={!validAmount} busy={createPayment.isPending} onClick={() => createPayment.mutate()}>{goPaySandbox ? "Pokračovat k testovací GoPay" : goPayActivationOnly ? "Otevřít ostrou GoPay bez dokončení" : "Zaplatit přes GoPay"}</Button>
              <p className="payment-note">{goPaySandbox ? "V sandboxu nedochází k připsání kreditu." : goPayActivationOnly ? "Aktivační režim nepovoluje zaúčtování ani zpětně." : goPayLive ? "Po návratu z GoPay ověříme výsledek platby." : ""} Bankovní převod zůstává doporučenou cestou bez poplatku.</p>
            </>
          )}
        </section>
      </div>
      {paymentDialogOpen && <PaymentStatusDialog payment={paymentStatus.data} loading={paymentStatus.isLoading || capabilities.isLoading} error={paymentStatus.error} activationOnly={goPayActivationOnly} onClose={dismissPaymentStatus} />}
    </div>
  );
}

function BadgeFlag({ currency }: { currency: string }) { return <span className="currency-badge">{currency}</span>; }

function CopyRow({ label, value, onCopy, highlight = false }: { label: string; value: string; onCopy?: (value: string, label: string) => void; highlight?: boolean }) {
  return <div className={highlight ? "highlight" : ""}><dt>{label}</dt><dd><strong>{value}</strong>{onCopy && <button type="button" onClick={() => onCopy(value, label)} aria-label={`Kopírovat ${label}`}><Copy /></button>}</dd></div>;
}


function PaymentStatusDialog({ payment, loading, error, activationOnly, onClose }: { payment?: GoPayPayment; loading: boolean; error: Error | null; activationOnly: boolean; onClose: () => void }) {
  const normalized = payment?.state.toUpperCase() ?? "UNKNOWN";
  const paid = normalized === "PAID";
  const canceled = normalized === "CANCELED" || normalized === "TIMEOUTED";
  const sandbox = payment?.environment !== "production";
  const recordOnly = activationOnly || (!sandbox && payment?.creditingState === "DISABLED");
  const activationComplete = recordOnly && !paid;
  const amount = payment?.amountCzk !== undefined ? ` ve výši ${payment.amountCzk.toLocaleString("cs-CZ")} Kč` : "";
  const title = loading
    ? "Ověřuji výsledek"
    : error
      ? "Výsledek se nepodařilo ověřit"
      : activationComplete
        ? "Kontrola brány dokončena"
        : paid
          ? (sandbox ? "Testovací platba byla úspěšná" : "Platba proběhla úspěšně")
          : canceled
            ? "Platba nebyla dokončena"
            : "Platba zatím nebyla dokončena";

  return (
    <Modal title={title} danger={Boolean(error || (!activationComplete && canceled) || (recordOnly && paid))} onClose={onClose} footer={<Button variant="primary" onClick={onClose}>Zavřít</Button>}>
      {loading ? (
        <div className="loading-panel"><span className="spinner" /> Ověřuji výsledek přímo u GoPay…</div>
      ) : error ? (
        <div className="form-error">{errorMessage(error)}</div>
      ) : payment ? (
        <div>
          {activationComplete ? (
            <>
              <p>Brána GoPay se úspěšně otevřela.</p>
              <p>Platba nebyla provedena ani připsána. Aktivační kontrola je dokončena.</p>
            </>
          ) : (
            <>
              <p>{paid ? `GoPay potvrdilo platbu${amount}.` : canceled ? "Platba nebyla dokončena. Můžete vytvořit novou platbu." : "Platba zatím nebyla dokončena. Pokud jste ji právě potvrdili, její zpracování může chvíli trvat."}</p>
              {paid && sandbox && <p>Testovací transakce nemění kredit a nevystavuje účetní doklad.</p>}
              {paid && recordOnly && <p>Aktivační režim platbu automaticky nepřipíše ke kreditu. Kontaktujte prosím podporu.</p>}
              {paid && !sandbox && !recordOnly && <p>{payment.credited ? "Částka byla připsána ke kreditu." : "Platbu jsme přijali. Připsání kreditu a vytvoření dokladu může chvíli trvat."}</p>}
            </>
          )}
        </div>
      ) : null}
    </Modal>
  );
}
